// CLI v0.8 — dev-mode tunnel management.
//
// Per decision 015 §"Tunnel mechanism (API-WebSocket-relay)" the dev runner
// no longer spawns `kubectl port-forward` to reach shared MongoDB / Redis /
// n8n. Instead it calls `IV2Client.openTunnel(service, localPort)`, which
// opens a local TCP listener bridged through the platform's `/tunnel/<svc>`
// WebSocket relay. This file is the runner-side glue: parse the shared
// service registry, allocate local ports, open one tunnel per entry, and
// expose the resolved env-var map alongside a `stop()` cleanup hook.
//
// Hard rules baked in:
//   1. No kubectl. The tunnel mechanism is now identical for every developer
//      regardless of whether kubectl is installed (decision 015 goal).
//   2. Identity env vars stay gone (decision 014 — `CLOUDGRID_TOKEN`,
//      `CLOUDGRID_ORG_ID`, `CLOUDGRID_ENTITY_ID` MUST NOT appear here; the
//      v2 client already has the JWT and the WS upgrade carries it).
//   3. Connection failures surface §23-voiced errors (auth / forbidden /
//      network) — see `mapTunnelError` below.

import { readFileSync } from 'node:fs';
import type * as net from 'node:net';
import { parse } from 'yaml';
import { CloudGridError, SHARED_SERVICES_PATH } from '../utils.js';
import { TunnelOpenError, type IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';

export interface TunnelEnvVars {
  MONGODB_URL: string;
  REDIS_URL: string;
  N8N_WEBHOOK_URL: string;
  AI_GATEWAY_URL: string;
}

export interface DevTunnels {
  envVars: TunnelEnvVars;
  stop(): Promise<void>;
}

/**
 * BL449 — extra context the runner passes so the tunnel can fetch a per-org
 * credentialed dev `MONGODB_URL`. Both optional: when `orgSlug` is absent the
 * tunnel falls back to the historical no-auth dev template (pre-BL449
 * behavior), keeping older call sites and tests working unchanged.
 */
export interface StartTunnelsOptions {
  /** Org slug for the `POST /orgs/:slug/dev-mongo-creds` lookup. */
  orgSlug?: string | null;
  /** Linked entity id — the server resolves its db name + scopes the grant. */
  entityId?: string;
}

/**
 * Redact the userinfo (user:password) from a `mongodb://` / `redis://` URL for
 * display. The child process still receives the real URL via env; only the
 * printed banner is redacted so a per-org dev password never lands in a
 * terminal, log, or screen-share (BL449 — no secrets logged).
 */
export function redactDbUrl(url: string): string {
  try {
    const u = new URL(url);
    if (u.username || u.password) {
      u.username = '***';
      u.password = '';
    }
    return u.toString();
  } catch {
    // Not a parseable URL — strip any `scheme://user:pass@` prefix defensively.
    return url.replace(/^([a-z][a-z0-9+.-]*:\/\/)[^@/]*@/i, '$1***@');
  }
}

interface SharedServiceEntry {
  env_var: string;
  local: string;
  production: string;
}

/**
 * Bind to port 0 and return the OS-assigned ephemeral port. Same shape as
 * v0.7 — the kubectl swap doesn't touch this helper.
 */
function findFreePort(): Promise<number> {
  // Use a dynamic import so test stubs of `net.createServer` are honoured.
  return import('node:net').then(
    (netMod) =>
      new Promise<number>((resolve, reject) => {
        const srv = netMod.createServer();
        srv.listen(0, () => {
          const addr = srv.address();
          if (!addr || typeof addr === 'string') {
            reject(new Error('Unexpected address from net.createServer'));
            return;
          }
          const port = addr.port;
          srv.close(() => resolve(port));
        });
      }),
  );
}

/**
 * Replace `{app-name}` in a templated URL with `entitySlug` and re-host the
 * resulting URL at `localhost:<localPort>`. The shared yaml's `local:` field
 * is kept K8s-shaped (e.g. `mongodb://mongodb-0.mongodb.sharedgrid-dev...`);
 * the runtime URL the user's child process sees is always `localhost`.
 */
function resolveLocalUrl(localTemplate: string, entitySlug: string, localPort: number): string {
  const url = localTemplate.replace('{app-name}', entitySlug);
  const parsed = new URL(url);
  parsed.hostname = 'localhost';
  parsed.port = String(localPort);
  return parsed.toString();
}

/** Read and parse `shared-services.yaml`. */
function loadSharedServices(): Record<string, SharedServiceEntry> {
  const raw = readFileSync(SHARED_SERVICES_PATH, 'utf-8');
  return parse(raw) as Record<string, SharedServiceEntry>;
}

/** Resolved dev DB connection templates (Mongo always; Redis per M4). */
interface DevDbTemplates {
  /** A `mongodb://` template; credentialed post-cutover, may carry `{app-name}`. */
  mongo: string;
  /** A `redis://` template; credentialed (M4) when the entity declares Redis. */
  redis: string;
}

/**
 * BL449 + M4 — resolve the dev Mongo and Redis connection templates in one
 * `getDevMongoCreds` call. When an org slug is known, ask the API for the
 * cutover-aware templates: pre-cutover it returns the no-auth Mongo template
 * (same as the registry's `mongodb.local`) and no `redis_url`; post-cutover it
 * returns a per-org credentialed Mongo template, plus — when the entity declares
 * `requires: [redis]` — a per-org credentialed `redis_url`. On any failure, or
 * when no org slug is available, or when the API omits `redis_url`, fall back to
 * the registry's no-auth template(s) so a transient issue or an out-of-date API
 * never breaks `cloudgrid dev`.
 */
async function resolveDevDbTemplates(
  client: IV2Client,
  services: Record<string, SharedServiceEntry>,
  opts: StartTunnelsOptions,
): Promise<DevDbTemplates> {
  const fallback: DevDbTemplates = {
    mongo: services['mongodb']?.local ?? '',
    redis: services['redis']?.local ?? '',
  };
  if (!opts.orgSlug) return fallback;
  try {
    const creds = await client.getDevMongoCreds(opts.orgSlug, opts.entityId);
    return {
      mongo: creds.url || fallback.mongo,
      redis: creds.redis_url || fallback.redis,
    };
  } catch (err) {
    process.stderr.write(devMongoCredsWarning(err));
    return fallback;
  }
}

/**
 * BL449 — compose the §23-voiced fallback warning printed when
 * `getDevMongoCreds` fails. The fallback itself is silent in the sense that it
 * never bypasses auth: the runner still hands the child process the no-auth
 * template, and Mongo rejects it if the dev pool now enforces auth. The text
 * exists only to point the user at the *likely* cause so they can self-serve.
 *
 * The two common post-cutover failures need different guidance:
 *   - HTTP 401 — the JWT expired; updating the CLI does nothing. Re-login.
 *   - HTTP 503 — the org has no dev creds minted yet; the user can't fix this
 *     themselves, an org admin has to provision the tier.
 * Anything else (network blip, 5xx) keeps the generic message with the detail.
 */
export function devMongoCredsWarning(err: unknown): string {
  const status = err instanceof V2ApiError ? err.status : undefined;
  if (status === 401) {
    return (
      'Could not fetch dev database credentials: your session is no longer authorized. ' +
      'Re-authenticate with `cloudgrid login`. Using the unauthenticated dev database for now.\n'
    );
  }
  if (status === 503) {
    return (
      'Dev database credentials are not yet provisioned for this grid. Contact your grid admin. ' +
      'Using the unauthenticated dev database for now.\n'
    );
  }
  const detail = err instanceof Error ? err.message : String(err);
  return (
    `Could not fetch dev database credentials (${detail}). ` +
    'Using the unauthenticated dev database for now.\n'
  );
}

/**
 * Map a {@link TunnelOpenError} (or generic Error) into a §23-voiced
 * `CloudGridError`. The runner surfaces the resulting message verbatim.
 *
 * `apiBaseUrl` is rendered into the network-error path so the user can
 * verify what the CLI just tried to reach — useful when DNS or VPN is
 * the underlying cause.
 */
function mapTunnelError(err: unknown, service: string, apiBaseUrl: string): CloudGridError {
  if (err instanceof TunnelOpenError) {
    if (err.kind === 'auth') {
      return new CloudGridError('Authentication failed for tunnel. Run: cloudgrid login');
    }
    if (err.kind === 'forbidden') {
      return new CloudGridError(
        `Not authorized to use the dev tunnel for service '${service}'. Confirm your grid membership.`,
      );
    }
    return new CloudGridError(
      `Tunnel to '${service}' failed: ${err.detail}. Verify the API is reachable at ${apiBaseUrl}.`,
    );
  }
  const detail = err instanceof Error ? err.message : String(err);
  return new CloudGridError(
    `Tunnel to '${service}' failed: ${detail}. Verify the API is reachable at ${apiBaseUrl}.`,
  );
}

/**
 * Open a local-TCP-over-WebSocket tunnel for every entry in
 * `shared-services.yaml`, then resolve env-var values pointing at the
 * resulting `localhost:<port>` endpoints. `AI_GATEWAY_URL` is HTTP-only and
 * does not tunnel — it's derived from `apiBaseUrl + '/ai-dev'` (decision
 * 015 §"Tunnel mechanism").
 *
 * If any tunnel fails to open, every already-opened tunnel is closed before
 * the error propagates so the caller never sees a half-open state.
 */
export async function startTunnels(
  client: IV2Client,
  entitySlug: string,
  apiBaseUrl: string,
  opts: StartTunnelsOptions = {},
): Promise<DevTunnels> {
  const services = loadSharedServices();
  const serviceNames = Object.keys(services);

  const ports: Record<string, number> = {};
  for (const name of serviceNames) {
    ports[name] = await findFreePort();
  }

  const opened: Array<{ name: string; server: net.Server }> = [];

  // Open tunnels sequentially. Parallel opens would speed things up by a
  // few hundred ms but make the cleanup-on-failure shape harder to reason
  // about and harder to test deterministically.
  for (const name of serviceNames) {
    try {
      const server = await client.openTunnel(name, ports[name]);
      opened.push({ name, server });
    } catch (err) {
      // Drain everything we already opened, then surface the §23 message.
      await Promise.all(
        opened.map(
          ({ server }) =>
            new Promise<void>((resolve) => {
              server.close(() => resolve());
              // `close()` doesn't tear active connections; in dev-mode
              // there are none yet (we just opened the listener), so
              // it returns quickly.
            }),
        ),
      );
      throw mapTunnelError(err, name, apiBaseUrl);
    }
  }

  // BL449 + M4 — resolve the dev Mongo and Redis templates. Pre-cutover the
  // server returns the no-auth Mongo template (identical to
  // `services.mongodb.local`) and no `redis_url`; post-cutover it returns a
  // per-org credentialed Mongo template and, when the entity declares Redis, a
  // per-org credentialed Redis template. On any error (e.g. an API older than
  // this CLI that lacks the endpoint) it falls back to the registry's no-auth
  // templates so a transient failure never breaks dev. The fetch happens once,
  // here, and only when an org slug is known.
  const devDb = await resolveDevDbTemplates(client, services, opts);

  // Build env-var map keyed off the registry's `env_var:` fields. The dev
  // MONGODB_URL / REDIS_URL carry per-org credentials post-cutover; both are
  // rehosted to `localhost:<port>` through the tunnel. The banner redacts them.
  const envVarMap: Record<string, string> = {};
  for (const name of serviceNames) {
    const entry = services[name];
    let template = entry.local;
    if (name === 'mongodb') template = devDb.mongo;
    else if (name === 'redis') template = devDb.redis;
    envVarMap[entry.env_var] = resolveLocalUrl(template, entitySlug, ports[name]);
  }

  const aiGatewayUrl = `${apiBaseUrl.replace(/\/$/, '')}/ai-dev`;
  const envVars: TunnelEnvVars = {
    MONGODB_URL: envVarMap['MONGODB_URL'] ?? '',
    REDIS_URL: envVarMap['REDIS_URL'] ?? '',
    N8N_WEBHOOK_URL: envVarMap['N8N_WEBHOOK_URL'] ?? '',
    AI_GATEWAY_URL: aiGatewayUrl,
  };

  let stopped = false;
  async function stop(): Promise<void> {
    if (stopped) return;
    stopped = true;
    await Promise.all(
      opened.map(
        ({ server }) =>
          new Promise<void>((resolve) => {
            server.close(() => resolve());
          }),
      ),
    );
  }

  return { envVars, stop };
}
